/**
 * 支付方式工具函数
 */
import type { Ref } from 'vue'
import { PAYMENT_METHOD_NAMES, PAYMENT_METHOD_PAY_TYPES, PaymentMethodEnum, TOPUP_API_ENDPOINTS } from '../constants'

// PaymentMethodEnum 已在 constants 中导出，此处不再重复导出

let externalPaymentRedirectPending = false

/**
 * 标记即将跳转到外部支付页。
 * 页面跳转会取消同一支付面板中尚未完成的商品列表请求；这些取消不应向用户展示为网络故障。
 */
export function markExternalPaymentRedirect() {
  externalPaymentRedirectPending = true
}

export function isExternalPaymentRedirectPending() {
  return externalPaymentRedirectPending
}

/**
 * 外部支付页返回时恢复正常错误提示。
 * 页面可能从 BFCache 恢复，模块级变量不会随着该恢复重新初始化。
 */
export function resetExternalPaymentRedirectPending() {
  externalPaymentRedirectPending = false
}

/**
 * 支付方式初始化和展示的通用规则
 *
 * 规则 1: PayPal 优先 → hasPayPalPasswordlessPayment === true 时优先展示 PayPal
 *   - 兜底：如果 PayPal 商品为空，则展示 paymentMethod=4 (Stripe) 的商品
 * 规则 2: 有订单历史 → hasPlacedOrder === true 时展示所有支付方式
 * 规则 3: 新用户默认 → 只展示 defaultDisplay = 1 的支付方式
 * 规则 4: 兜底策略 → 若无 defaultDisplay = 1 的支付方式，展示所有支付方式
 */

/**
 * 初始化支付方式
 */
export function initializePaymentMethod(
  currentPaymentMethod: Ref<number>,
  productLists: Ref<any[]>,
  hasPayPalPasswordlessPayment: Ref<boolean>,
  hasPlacedOrder: Ref<boolean>,
) {
  // 规则 1: PayPal 优先
  if (hasPayPalPasswordlessPayment.value) {
    // 检查是否有 PayPal 商品
    const paypalLists = productLists.value.filter(list => list.paymentMethod === PaymentMethodEnum.PAYPAL)
    if (paypalLists.length > 0) {
      currentPaymentMethod.value = PaymentMethodEnum.PAYPAL
    }
    else {
      // 兜底：使用 Stripe
      const stripeLists = productLists.value.filter(list => list.paymentMethod === PaymentMethodEnum.STRIPE)
      currentPaymentMethod.value = stripeLists[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
    }
    return
  }
  if (hasPlacedOrder.value) {
    // 规则 2: 有订单历史，默认选择第一个支付方式
    currentPaymentMethod.value = productLists.value[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
    return
  }

  // 规则 3: 根据订单历史和 defaultDisplay 确定
  const defaultPaymentMethods = productLists.value.filter(
    list => list.defaultDisplay === 1,
  )

  if (defaultPaymentMethods.length > 0) {
    // 选择第一个默认展示的支付方式
    currentPaymentMethod.value = defaultPaymentMethods[0].paymentMethod
  }
  else {
    // 规则 4: 兜底：选择第一个支付方式
    currentPaymentMethod.value = productLists.value[0]?.paymentMethod || PaymentMethodEnum.PAYPAL
  }
}

/**
 * 创建可用的支付方式列表 computed
 */
export function createAvailablePaymentMethods(
  productLists: Ref<any[]>,
  hasPayPalPasswordlessPayment: Ref<boolean>,
  hasPlacedOrder: Ref<boolean>,
) {
  return () => {
    const lists = productLists.value

    // 规则 1: PayPal 无密码支付优先
    if (hasPayPalPasswordlessPayment.value) {
      const paypalLists = lists.filter(list => list.paymentMethod === PaymentMethodEnum.PAYPAL)

      // 兜底：如果 PayPal 商品为空，则展示 Stripe 商品
      if (paypalLists.length === 0) {
        const stripeLists = lists.filter(list => list.paymentMethod === PaymentMethodEnum.STRIPE)
        return stripeLists
      }

      return paypalLists
    }

    // 规则 2: 有订单历史
    if (hasPlacedOrder.value)
      return lists

    // 规则 3: 新用户默认展示
    const defaultDisplayLists = lists.filter(list => list.defaultDisplay === 1)

    if (defaultDisplayLists.length > 0)
      return defaultDisplayLists

    // 规则 4: 兜底：如果没有 defaultDisplay=1 的商品（即都为 0），展示所有商品
    return lists
  }
}

/**
 * 获取支付方式名称
 */
export function getPaymentChannelName(paymentMethod: number): string {
  return PAYMENT_METHOD_NAMES[paymentMethod] || 'paypal'
}

/**
 * 切换支付方式
 */
export function createSelectPaymentMethod(currentPaymentMethod: Ref<number>) {
  return (method: number) => {
    currentPaymentMethod.value = method
  }
}

/**
 * 根据支付方式获取充值 API 端点
 */
export function getTopupPaymentApi(paymentMethod: number): string {
  return TOPUP_API_ENDPOINTS[paymentMethod] || TOPUP_API_ENDPOINTS[PaymentMethodEnum.PAYPAL]
}

/**
 * 根据支付方式获取 payType
 */
export function getPayType(paymentMethod: number): number {
  return PAYMENT_METHOD_PAY_TYPES[paymentMethod] || 6
}

/**
 * 进入外部支付（如 PayPal）前，同步在当前阅读页 URL 标记 recommend 参数。
 * 必须用 history.replaceState 同步写入：紧随其后的 `window.location.href = paypalUrl` 是同步跳转，
 * 若改用异步的 router.replace 会来不及写入。用户未支付返回（往返缓存 reload / 取消回跳）时，
 * 阅读页据此参数展示推荐书籍模块。
 * @returns 写入后的当前页完整 URL，可作为 redirect_source / successUrl 使用
 */
export function markRecommendParam(contentChapterId?: string | number): string {
  const url = new URL(window.location.href)
  url.searchParams.set('recommend', '1')
  if (contentChapterId !== undefined && contentChapterId !== null && String(contentChapterId))
    url.searchParams.set('recommendChapterId', String(contentChapterId))
  history.replaceState(history.state, '', url.href)
  return url.href
}
